import {
  WixPatternsContainer,
  QueryState,
  ReportBI,
  syncWithSharedQuery,
  TaskState,
} from '@wix/bex-core';
import { ToolbarCollectionState } from '../ToolbarCollectionState';
import { InfiniteScrollTableState } from '../InfiniteScrollTableState';
import { TableColumn } from '../../model';
import { NestedTableColumn } from './NestedTableColumn';
import { NestedTableNestedModeState } from './NestedTableNestedModeState';
import { NestedTableFlatModeState } from './NestedTableFlatModeState';
import { NestedCollectionFetchAllState } from './NestedCollectionFetchAllState';
import { NestedTableLevelDescriptor } from './NestedTableLevelDescriptor';
import { EagerLazyModeOptions } from './types';
import { addEventListener } from '@wix/bex-core/util';
import { reaction } from 'mobx';

export interface NestedTableStateBaseParams<C extends string> {
  /**
   * If the total items in the server are less or equal to this threshold, all items will be fetched and rendered at once, in expanded state (referred to as "Eager" mode).
   * @default 1000
   */
  fetchAllThreshold?: number;
  /**
   * In "Eager" mode, items will be in expanded state. This threshold allows to use collapsed state if the total items in the server are greater than this threshold.
   * @default 0
   */
  expandAllThreshold?: number;
  /**
   * Columns to be rendered in the nested table. Same as [regular table columns](/pages/cairo/?path=%2Fstory%2Fcomponents-collection--table&tab=API) - without rendering logic of cells (defined for each level individually in the `levels` prop).
   */
  columns: NestedTableColumn<C>[];
  /**
   * Main column of the nested table.
   * An object with the following properties:
   *  - `title`: The title of the column. @default ''
   *  - `width`: The width of the column. Can be a number (in pixels) or a string (percentage). @default '326px'
   */
  mainColumn?: {
    title?: string;
    width?: number | string;
  };
  /**
   * Defines the collection for each level in the nested table.
   * Each level needs to specify:
   *   - `createCollection`: A function that creates a collection for the current level (using `useCreateCollection` hook), and returns the collection state and a reference to a filter object within this collection that filters the collection by the parent key.
   *   - `parentKey`: A function that accepts the item of the current level and returns the key of the parent item, for example: `(item) => item.parentId`.
   *   - `breadcrumbs`: A function that accepts the item of the current level and returns an array of breadcrumbs to be displayed in the toolbar. Each breadcrumb is an object with `id` and `name` properties. `fetchData` will be called with `query.fields` that contains the value `breadcrumbs` to signal that the breadcrumbs should be fetched.
   *   - `columns` (optional): Render logic of the cells for this level. By default all cells will be empty.
   *   - `renderMainColumn` (optional): Render logic of the main column for this level.
   *   - `actionsCell` (optional): Adds a [`<TableActionCell />`](https://www.docs.wixdesignsystem.com/?path=/story/components-lists-table--tableactioncell) as last column. Must be a [ActionsCellProps](/pages/cairo/?path=/story/components-collection-features--actionscellprops) object, or a function that returns a [ActionsCellProps](/pages/cairo/?path=/story/components-collection-features--actionscellprops) object.<
   */
  levels: Omit<NestedTableLevelDescriptor<C, any, any>, 'depth'>[];
}

export interface NestedTableStateParams<C extends string>
  extends NestedTableStateBaseParams<C> {
  container: WixPatternsContainer;
}

export class NestedTableState<C extends string = string> {
  readonly container;
  readonly reportBi: ReportBI;
  readonly toolbar;
  readonly tableState;
  readonly mainColumn;
  readonly options: EagerLazyModeOptions;

  readonly initTask = new TaskState();

  readonly levels: NestedTableLevelDescriptor<C, any, any>[];

  readonly rootNodeCollection;
  readonly query;

  readonly nested: NestedTableNestedModeState<C>;
  readonly flat: NestedTableFlatModeState<C>;

  constructor(params: NestedTableStateParams<C>) {
    this.container = params.container;

    this.query = new QueryState({
      filters: {},
    });

    this.levels = params.levels.map((level, i) => ({
      ...level,
      depth: i,
    }));

    this.options = {
      fetchAllThreshold: params.fetchAllThreshold ?? 1000,
      expandAllThreshold: params.expandAllThreshold ?? 0,
    };

    this.mainColumn = params.mainColumn;
    const rootLevelDescriptor = this.levels[0];

    if (rootLevelDescriptor == null) {
      throw new Error('NestedTableState must have at least one level');
    }

    this.rootNodeCollection = rootLevelDescriptor.createCollection();

    const { collection } = this.rootNodeCollection;

    syncWithSharedQuery(collection.query, this.query);

    this.reportBi = (reportProps) => {
      const { isFetchAllAborted } = this;
      this.container.reportBi({
        ...reportProps,
        params: {
          ...reportProps.params,
          ...(isFetchAllAborted != null && {
            componentStatus: isFetchAllAborted
              ? 'All items collapsed'
              : 'All items expanded',
          }),
        },
      });
    };

    this.toolbar = new ToolbarCollectionState({
      collection: this.rootNodeCollection.collection,
      container: params.container,
      componentType: 'NestedTable',
      reportBi: this.reportBi,
    });

    this.toolbar.setNewColumns(
      params.columns.map((column) => ({
        ...column,
        render: () => null,
      })) as TableColumn<unknown>[],
    );

    this.tableState = new InfiniteScrollTableState({
      collection: this.rootNodeCollection.collection,
      container: params.container,
      toolbar: this.toolbar,
    });

    this.nested = new NestedTableNestedModeState({
      wrapper: this,
      reportBi: this.reportBi,
    });

    this.flat = new NestedTableFlatModeState({
      wrapper: this,
    });
  }

  get isFetchAllAborted() {
    return this.nested.isFetchAllAborted;
  }

  get expandAll() {
    return this.nested.expandAll;
  }

  get nestedCollection() {
    return this.nested.nestedCollection;
  }

  _attemptFetchAllAndPopulateCache() {
    const initTask = new TaskState();
    return {
      initTask,
      init: () => {
        initTask.runOnce(async () => {
          const fetchAllState = new NestedCollectionFetchAllState({
            options: this.options,
            query: this.nested.query,
            nestedCollectionState: this.nested.nestedCollection,
          });

          const disposers = [fetchAllState.init()];

          try {
            await fetchAllState.initTask.status.promise;
          } finally {
            disposers.forEach((d) => d());
          }
        });

        return () => {};
      },
    };
  }

  get isFlatMode() {
    return this.query.hasActiveFilters;
  }

  init() {
    const { initTask, tableState, query, rootNodeCollection, flat } = this;

    const attemptFetchAllState = this._attemptFetchAllAndPopulateCache();

    const disposers = [
      query.init(),
      reaction(
        () => this.isFlatMode,
        (isFlatMode) => {
          const {
            query: { _fields },
          } = this;
          if (isFlatMode) {
            _fields.add('breadcrumbs');
          } else {
            _fields.delete('breadcrumbs');
          }
        },
        { fireImmediately: true },
      ),
      addEventListener(
        rootNodeCollection.collection.emitter,
        'beforeInitialFetch',
        () => attemptFetchAllState.initTask.status.promise,
      ),
      ...flat.sections.map(({ collection }) =>
        addEventListener(
          collection.emitter,
          'beforeInitialFetch',
          () => attemptFetchAllState.initTask.status.promise,
        ),
      ),
      attemptFetchAllState.init(),
      tableState.init({
        featuresInitializers: [],
      }),
    ];

    initTask.runOnce(async () => {
      tableState.initTask.runOnce();
      attemptFetchAllState.initTask.runOnce();
      await attemptFetchAllState.initTask.status.promise;
      await tableState.initTask.status.promise;
    });

    return () => {
      initTask.reset();
      disposers.forEach((d) => d());
    };
  }
}
